import {
  MinusIcon,
  PanelRightClose,
  PanelRightOpen,
  PlusIcon,
  RefreshCw,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';


interface PdfPreviewToolbarProps {
  scale: number;
  isPdfStale: boolean;
  isLoadingPdf: boolean;
  onRegenerateNow: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFitPage: () => void;
  onFitWidth: () => void;
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
}

/**
 * Toolbar above the PDF viewer: the "Preview" label + stale-preview regenerate
 * indicator on the left, and the zoom controls (out / percentage / in / fit
 * width) plus the mobile-only sidebar toggle on the right.
 */
export function PdfPreviewToolbar({
  scale,
  isPdfStale,
  isLoadingPdf,
  onRegenerateNow,
  onZoomIn,
  onZoomOut,
  onFitPage,
  onFitWidth,
  sidebarOpen,
  onToggleSidebar,
}: PdfPreviewToolbarProps) {
  const { t } = useTranslation();

  if (isLoadingPdf) return null

  return (
    <div className="  px-3 sm:px-6 absolute bottom-4 left-0 z-999 py-2 sm:py-3 flex flex-wrap items-center justify-between gap-2 sm:gap-3">
      <div className="flex items-center gap-2 sm:gap-3">
        {/* Preview-updating indicator — click to regenerate immediately. */}
        {isPdfStale && !isLoadingPdf && (
          <button
            onClick={onRegenerateNow}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-selise-blue hover:underline"
            title={t('GENERATEREVIEW_PREVIEW_REGENNOW', { defaultValue: 'Regenerate now' })}
          >
            <RefreshCw className="w-3 h-3 animate-spin" />
            <span className="hidden sm:inline">
              {t('GENERATEREVIEW_PREVIEW_UPDATING', { defaultValue: 'Preview updating…' })}
            </span>
          </button>
        )}
      </div>

      <div className="flex items-center gap-2">
        <div className="flex items-center border border-border rounded-sm bg-[hsl(var(--bg))] shadow-sm">
          <button
            onClick={onZoomOut}
            disabled={scale <= 0.5}
            className="h-9 w-9 sm:h-auto sm:w-auto sm:p-2 inline-flex items-center justify-center text-[hsl(var(--fg))] hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors rounded-l-lg"
            title={t('GENERATEREVIEW_ZOOMOUT')}
          >
            <MinusIcon className="size-4" />
          </button>
          <button
            type="button"
            onClick={onFitPage}
            className="text-xs font-bold text-[hsl(var(--fg))] min-w-14 text-center px-3 h-9 sm:h-auto sm:py-2 hover:bg-muted transition-colors"
            title="100%"
          >
            {Math.round(scale * 100)}%
          </button>
          <button
            onClick={onZoomIn}
            disabled={scale >= 2.0}
            className="h-9 w-9 sm:h-auto sm:w-auto sm:p-2 inline-flex items-center justify-center text-[hsl(var(--fg))] hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title={t('GENERATEREVIEW_ZOOMIN')}
          >
            <PlusIcon className="size-4" />
          </button>
          <span className="w-px h-5 bg-secondary-100"></span>
          <button
            onClick={onFitWidth}
            className="px-3 h-9 sm:h-auto sm:py-2 text-[10px] sm:text-xs font-bold uppercase tracking-wide text-muted-foreground hover:bg-muted hover:text-[hsl(var(--fg))] transition-colors rounded-r-lg"
            title={t('GENERATEREVIEW_ACTIONS_FITWIDTH')}
          >
            {t('GENERATEREVIEW_ACTIONS_FITWIDTH')}
          </button>
        </div>
        {/* Drawer toggle — mobile only. */}
        <button
          onClick={onToggleSidebar}
          aria-label={sidebarOpen ? t('GENERATEREVIEW_HIDESIDEBAR') : t('GENERATEREVIEW_SHOWSIDEBAR')}
          className="md:hidden h-9 w-9 inline-flex items-center justify-center text-[hsl(var(--fg))] border border-border rounded-md hover:bg-muted transition-colors cursor-pointer"
          title={sidebarOpen ? t('GENERATEREVIEW_HIDESIDEBAR') : t('GENERATEREVIEW_SHOWSIDEBAR')}
        >
          {sidebarOpen ? (
            <PanelRightClose className="w-4 h-4" />
          ) : (
            <PanelRightOpen className="w-4 h-4" />
          )}
        </button>
      </div>
    </div>
  );
}
